import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from 'react-sortable-hoc'
import moment from 'moment'
import classNames from 'classnames'
import { nanoid } from 'nanoid'
import numeral from 'numeraljs'

import { ButtonRow } from './button-row'
import Button from './button'
import Loader, { Preloader } from './loader'
import OrderArrow from './order-arrow'
import Pagination from './pagination'
import SelectBox from './select-box'
import Table, { TableLoadingRows } from './table'
import TopScrollbar from './top-scrollbar'
import { dateFormatShort } from '../core/variables'
import { paramNameMapper } from '../helpers/report-module'
import styles from '../styles/lost-links-table.module.scss'
import { GetLostLinksTableDataQuery } from '../__gql-types__/graphql'

interface ElementProps {
  paramHeading: string
}

const SortableItem = SortableElement(({ paramHeading }: ElementProps) => {
  return <span>{paramNameMapper[paramHeading] || paramHeading}</span>
})

const SortableWrapper = SortableContainer(
  ({ rowsData, parameterHeadings, showEmptyColumns, children }) => {
    return (
      <tr>
        {children}
        {parameterHeadings.map((paramHeading, paramIndex) => {
          const fullColumnData = rowsData.map((r) => r[paramHeading])

          // Don't show column if it has no data
          if (!showEmptyColumns && fullColumnData.every((d) => !d)) {
            return null
          }

          return (
            <th key={paramHeading} className={styles.headerCell}>
              <SortableItem
                index={paramIndex}
                key={nanoid()}
                // @ts-ignore
                paramHeading={paramHeading}
              />
            </th>
          )
        })}
      </tr>
    )
  },
)

interface FullRowData {
  firstObserved: string
  landingPage: string
  [metricOrParamName: string]: string | number
}

export interface PaginationData {
  totalRows: number
  rowsPerPage: number
  pages: number
  activePage: number
  sortDirection: 'ASC' | 'DESC'
}

interface LostLinksTableProps {
  data?: GetLostLinksTableDataQuery
  loading?: boolean
  error?: boolean
  totalRows?: number
  paginationData: PaginationData | null
  setPaginationData: React.Dispatch<React.SetStateAction<PaginationData | null>>
  refetchData: (pagination: PaginationConfig) => void
  showEmptyColumns?: boolean
}

// Only for GA data for now
// Adobe data is possible, but unused
// https://github.com/uplifter-limited/uplifter-graphql-backend/issues/323
const LostLinksTable = ({
  data,
  loading,
  error,
  totalRows,
  paginationData,
  setPaginationData,
  refetchData,
  showEmptyColumns = true,
}: LostLinksTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null)

  const tableData = useMemo(() => {
    if (!data) return null

    return data.report.linkAudit.linkAuditTable
  }, [data])

  const metricHeading = useMemo(() => {
    if (!tableData) return ''

    return tableData.metricHeadings[0] || 'Metric'
  }, [tableData])

  const [parameterColumnIndexOrder, setParameterColumnIndexOrder] = useState<
    number[]
  >([])

  // Set the initial parameter column order
  useEffect(() => {
    if (!tableData || parameterColumnIndexOrder.length > 0) return

    setParameterColumnIndexOrder(
      Array.from(new Array(tableData.parameterHeadings.length), (_, i) => i),
    )
  }, [tableData])

  // Table data should be returned in order determined by current parameterColumnIndexOrder
  const { parameterHeadings, rowsData } = useMemo(() => {
    if (!tableData) {
      return { parameterHeadings: [], rowsData: [] }
    }

    const orderedParamsHeadingsData: string[] = []
    const fullRowsData: FullRowData[] = []

    const {
      firstObserved,
      landingPageList,
      metricHeadings,
      metricValues,
      parameterHeadings: _parameterHeadings,
      parameterValues,
    } = tableData

    parameterColumnIndexOrder.forEach((paramIndex) => {
      orderedParamsHeadingsData.push(_parameterHeadings[paramIndex])
    })

    firstObserved.forEach((item, index) => {
      const rowData = {
        firstObserved: item,
        landingPage: landingPageList[index],
        [metricHeadings ? metricHeadings[0] : 'Metric']: metricValues[0][index],
      }

      parameterColumnIndexOrder.forEach((paramIndex) => {
        rowData[_parameterHeadings[paramIndex]] =
          parameterValues[paramIndex][index]
      })

      fullRowsData.push(rowData)
    })

    return {
      parameterHeadings: orderedParamsHeadingsData,
      rowsData: fullRowsData,
    }
  }, [tableData, parameterColumnIndexOrder])

  const firstItemIndex = useMemo(() => {
    return paginationData
      ? paginationData.rowsPerPage * (paginationData.activePage - 1) + 1
      : 1
  }, [paginationData])

  // Reset pagination data when table data changes
  useEffect(() => {
    if (!tableData) return

    const _totalRows = totalRows || tableData?.pageData?.total

    if (_totalRows === undefined) return

    setPaginationData({
      ...(paginationData && _totalRows === paginationData.totalRows
        ? paginationData
        : {
            rowsPerPage: 10,
            activePage: 1,
            sortDirection: 'DESC',
          }),
      totalRows: _totalRows,
      pages: Math.ceil(_totalRows / (paginationData?.rowsPerPage || 10)),
    })
  }, [tableData, totalRows])

  return (
    <>
      <div className={styles.tableContainer}>
        <TopScrollbar className={styles.tableContainerInner}>
          <Table ref={tableRef} className={styles.table}>
            <thead>
              {parameterHeadings.length > 0 ? (
                // @ts-ignore
                <SortableWrapper
                  rowsData={rowsData}
                  parameterHeadings={parameterHeadings}
                  showEmptyColumns={showEmptyColumns}
                  helperClass={styles.itemDragged}
                  lockToContainerEdges
                  axis="x"
                  lockAxis="x"
                  distance={5}
                  onSortOver={(vals) => {
                    const { oldIndex, newIndex } = vals

                    if (oldIndex !== newIndex) {
                      const orderedArray = arrayMove(
                        parameterColumnIndexOrder,
                        oldIndex,
                        newIndex,
                      )

                      setParameterColumnIndexOrder(orderedArray)
                    }
                  }}
                >
                  <>
                    <th>First observed</th>
                    <th>
                      <Button
                        variant="iconOnly"
                        className={styles.sortButton}
                        onPress={() => {
                          if (data && paginationData) {
                            const newSortDirection =
                              paginationData?.sortDirection === 'ASC'
                                ? 'DESC'
                                : 'ASC'

                            setPaginationData({
                              ...paginationData,
                              activePage: 1,
                              sortDirection: newSortDirection,
                            })

                            refetchData({
                              limit: paginationData.rowsPerPage,
                              offset: 1,
                              sortDirection: newSortDirection,
                            })
                          }
                        }}
                      >
                        <span>
                          {metricHeading || (
                            <Loader className={styles.loadingText} />
                          )}
                        </span>
                        <OrderArrow
                          sortKey={metricHeading}
                          currentKey={metricHeading || 'none'}
                          orderAsc={paginationData?.sortDirection === 'ASC'}
                          className={styles.orderArrow}
                        />
                      </Button>
                    </th>
                    <th className={styles.landingPage}>
                      Landing page with non-utm parameters
                    </th>
                  </>
                </SortableWrapper>
              ) : (
                <tr>
                  <th>First observed</th>
                  <th>
                    <Button
                      variant="iconOnly"
                      className={styles.sortButton}
                      onPress={() => {
                        if (data && paginationData) {
                          const newSortDirection =
                            paginationData?.sortDirection === 'ASC'
                              ? 'DESC'
                              : 'ASC'

                          setPaginationData({
                            ...paginationData,
                            activePage: 1,
                            sortDirection: newSortDirection,
                          })

                          refetchData({
                            limit: paginationData.rowsPerPage,
                            offset: 1,
                            sortDirection: newSortDirection,
                          })
                        }
                      }}
                    >
                      <span>
                        {metricHeading || (
                          <Loader className={styles.loadingText} />
                        )}
                      </span>
                      <OrderArrow
                        sortKey={metricHeading}
                        currentKey={metricHeading || 'none'}
                        orderAsc={paginationData?.sortDirection === 'ASC'}
                        className={styles.orderArrow}
                      />
                    </Button>
                  </th>
                  <th className={styles.landingPage}>
                    Landing page with non-utm parameters
                  </th>
                  <>
                    {Array.from(new Array(5), (_, index) => index).map((i) => {
                      return (
                        <th key={nanoid()}>
                          <Loader className={styles.loadingText} />
                        </th>
                      )
                    })}
                  </>
                </tr>
              )}
            </thead>
            {!error && loading ? (
              <tbody>
                <TableLoadingRows colCount={8} />
              </tbody>
            ) : (
              <>
                {error || rowsData.length === 0 ? (
                  <tbody
                    className={classNames(styles.tableBody, styles.emptyData)}
                  >
                    <tr>
                      <td
                        colSpan={
                          parameterHeadings.length > 0
                            ? parameterHeadings.length + 3
                            : 8
                        }
                      >
                        <div>
                          <b />
                          <p>
                            {error ? 'Error fetching data' : 'No links found.'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {rowsData.map((row) => {
                      return (
                        <tr key={JSON.stringify(row)}>
                          <td>
                            <span>
                              {moment(row.firstObserved).format(
                                dateFormatShort,
                              )}
                              {row.sessionSource === 'google' &&
                                row.sessionMedium === 'cpc' && (
                                  <>
                                    <br />
                                    <span className={styles.specialTag}>
                                      Google Ads?
                                    </span>
                                  </>
                                )}
                            </span>
                          </td>
                          <td className={styles.metricColumn}>
                            <span>
                              {numeral(row[metricHeading]).format('0,0')}
                            </span>
                          </td>
                          <td className={styles.landingPage}>
                            <span>{row.landingPage}</span>
                          </td>
                          {parameterHeadings.map((param) => {
                            const fullColumnData = rowsData.map((r) => r[param])

                            // Don't show column if it has no data
                            if (
                              !showEmptyColumns &&
                              fullColumnData.every((d) => !d)
                            ) {
                              return null
                            }

                            return (
                              <td key={param} className={styles.paramColumn}>
                                <span
                                  className={
                                    row[param] ? undefined : styles.noData
                                  }
                                >
                                  {row[param] || ''}
                                </span>
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                )}
              </>
            )}
          </Table>
        </TopScrollbar>
      </div>
      {paginationData && (
        <>
          {loading ? (
            <Preloader />
          ) : (
            <>
              {/* TODO: Turn this into a new component */}
              <ButtonRow className={styles.paginationRow}>
                <Pagination
                  pages={paginationData.pages}
                  activePage={paginationData.activePage}
                  onChange={(index: number) => {
                    setPaginationData({
                      ...paginationData,
                      activePage: index,
                    })

                    refetchData({
                      limit: paginationData.rowsPerPage,
                      offset: (index - 1) * paginationData.rowsPerPage + 1,
                      sortDirection: paginationData.sortDirection,
                    })
                  }}
                />
                {paginationData.totalRows > 10 && (
                  <span className={styles.rowsLabelWrapper}>
                    <span className={styles.paginationSummary}>
                      {numeral(firstItemIndex).format('0,0')}-
                      {numeral(
                        Math.min(
                          paginationData.totalRows,
                          firstItemIndex + paginationData.rowsPerPage - 1,
                        ),
                      ).format('0,0')}{' '}
                      of {numeral(paginationData.totalRows).format('0,0')}
                    </span>
                    <SelectBox
                      isSearchable={false}
                      defaultValue={{
                        label: '10',
                        value: '10',
                      }}
                      value={{
                        label: paginationData.rowsPerPage.toString(),
                        value: paginationData.rowsPerPage.toString(),
                      }}
                      options={[
                        { label: '10', value: '10' },
                        { label: '25', value: '25' },
                        { label: '50', value: '50' },
                        {
                          label: '100',
                          value: '100',
                        },
                        {
                          label: '150',
                          value: '150',
                        },
                      ]}
                      onChange={(newValue) => {
                        if (!newValue) return

                        const newRowsPerPage = parseInt(newValue.value, 10)

                        setPaginationData({
                          ...paginationData,
                          pages: Math.ceil(
                            paginationData.totalRows / newRowsPerPage,
                          ),
                          activePage: 1,
                          rowsPerPage: newRowsPerPage,
                        })

                        refetchData({
                          offset: 1,
                          limit: newRowsPerPage,
                          sortDirection: paginationData.sortDirection,
                        })
                      }}
                    />
                  </span>
                )}
              </ButtonRow>
            </>
          )}
        </>
      )}
    </>
  )
}

export default LostLinksTable
